import styled from '@emotion/styled';
import { FC } from 'react';
import { TrainingCourseProgressInfo } from '@taleemabad/db';
import {
  CourseProgressBanner,
  ProgressInformationProps,
  Spacing,
  IconText,
  Typography,
  List,
} from '@taleemabad/dsm/components';
import { BreakpointSizes, mq } from '@taleemabad/dsm/themes';

interface Category {
  id: string;
  name: string;
  description: string;
  image: string;
  courseCount?: number;
}
interface TeacherTrainingCoursesCategoriztionProps {
  courseProgressBanner?: TrainingCourseProgressInfo | null;
  // Optional becuase we have to show it when training levels are not available
  onTrainingCourseProgressBannerClick?: (
    course: TrainingCourseProgressInfo | null,
  ) => void;
  progressInformation: ProgressInformationProps;
  onCourseCategoryClick: (course: string) => void;
  filteredCategories: Category[];
  courseLevel?: string | null;
}

export const TeacherTrainingCoursesCategorization: FC<
  TeacherTrainingCoursesCategoriztionProps
> = ({
  courseProgressBanner,
  onTrainingCourseProgressBannerClick,
  onCourseCategoryClick,
  filteredCategories,
  courseLevel,
}) => {
  return (
    <TeacherTrainingCourseContainer>
      {courseProgressBanner && !courseProgressBanner.allTrainingsCompleted && (
        <Typography
          data-cy="teacher-traning-subtitle"
          variant="textTitleLargePrimary40"
        >
          {courseProgressBanner.progressText}
        </Typography>
      )}
      {courseProgressBanner && (
        <ProgressContainer data-cy="teacher-training-progress-banner">
          <CourseProgressBanner
            trainingCourseProgressInfo={courseProgressBanner}
            onTrainingCourseProgressBannerClick={(course) =>
              onTrainingCourseProgressBannerClick?.(course)
            }
          ></CourseProgressBanner>
        </ProgressContainer>
      )}

      <Spacing marginTop="xxl">
        <StickyContainer>
          <HeadlineContainer>
            {courseLevel && (
              <Typography variant="textHeadlineMediumOnSurface">
                {courseLevel ? `Level ${courseLevel}` : 'Courses'}
              </Typography>
            )}
            <Typography
              variant="textBodyLargeNeutral10"
              data-cy="teacher-training-courses-headline"
            >
              Select a course category below:
            </Typography>
          </HeadlineContainer>
        </StickyContainer>
        <StyledCategoryContainer>
          {filteredCategories?.map((category) => (
            <List
              onClick={() => onCourseCategoryClick(category.id)}
              key={category.id}
              alignment="center"
              heading={category.name}
              supportingText={
                category.courseCount && (
                  <IconText
                    icon="import_contacts"
                    text={`${category.courseCount} courses`}
                  />
                )
              }
              data-cy="teacher-training-main-courses"
              leadingIcon={{
                type: 'image',
                direction: 'leading',
                source: category.image,
              }}
            />
          ))}
        </StyledCategoryContainer>
      </Spacing>
    </TeacherTrainingCourseContainer>
  );
};

const TeacherTrainingCourseContainer = styled.div``;

const ProgressContainer = styled.div`
  display: flex;
  background-color: var(--md-sys-color-surface-container-highest);
  margin-top: var(--rk-spacing-xl);
  ${mq(BreakpointSizes.xs)} {
    flex-direction: column;
    background-color: unset;
  }
`;
const StyledCategoryContainer = styled.div`
  display: grid;
  grid-template-columns: repeat(2, 1fr);

  gap: var(--rk-spacing-xl);
  ${mq(BreakpointSizes.xs)} {
    display: flex;
    flex-direction: column;
  }
`;

const StickyContainer = styled.div`
  position: sticky;
  top: 0;
  z-index: 10;
  background: var(--md-sys-color-on-primary);
`;

const HeadlineContainer = styled.div`
  display: flex;
  flex-direction: column;
  justify-content: space-between;
  margin-bottom: var(--rk-spacing-xl);
  gap: var(--rk-spacing-xxl);
  ${mq(BreakpointSizes.xs)} {
    flex-direction: column;
  }
`;
